import {
  type BaseTestInstanceResponseMark,
  MarkStatus
} from 'services/assist-api/entities/test-instance-response-mark/types';

/**
 * Unscorable types defined by Writeshift (https://docs.writeshift.com/#post-essay-score) as well as
 * one Assist specific type (TOO_SHORT_TO_SEND). OK is considered an unscorable type by Writeshift
 * even though it represents an essay that was successfully scored.
 */
const unscorableTypes = [
  // Writeshift defined:
  'OK',
  'TOO_SHORT',
  'OFF_TOPIC',
  'REPETITIOUS',
  'INSUFFICIENT_DEVELOPMENT',
  'UNKNOWN_WORDS',
  'BAD_SYNTAX',
  'VIOLENT',
  'COPIED_QUESTION',

  // Assist defined:
  'TOO_SHORT_TO_SEND'
] as const;

/**
 * Failure codes defined by Writeshift: https://docs.writeshift.com/#understanding-the-response
 *
 * These are system errors related to sending data to the Assist API rather than representing the
 * marking of the response but technically these could be displayed.
 */
const failureCodes = [
  'INTERNAL_ERROR',
  'INVALID_REQUEST',
  'ORGANIZATION_NOT_FOUND',
  'ORGANIZATION_NOT_SUBSCRIBED',
  'AUTHOR_NOT_PROVIDED',
  'AUTHOR_NOT_FOUND',
  'DOCUMENT_NOT_PROVIDED',
  'DOCUMENT_NOT_FOUND',
  'SETTINGS_NOT_PROVIDED',
  'OUT_OF_CREDIT',
  'NONEXISTANT_SERVICE',
  'NOT_OPTED_IN',
  'TOO_SHORT',
  'IN_PROGRESS',
  'MODEL_NOT_AVAILABLE',
  'TEXT_DATA_NOT_PROVIDED'
] as const;

type UnscorableTypeOrFailureCode = (typeof failureCodes | typeof unscorableTypes)[number];

const codeWithHyphens = (code: UnscorableTypeOrFailureCode): string => `-${code}-`;
const codeFromAutoscoredComment = (
  autoscoredComment: string
): UnscorableTypeOrFailureCode | undefined =>
  [...unscorableTypes, ...failureCodes].find((c) => autoscoredComment.includes(codeWithHyphens(c)));

/**
 * Converts an AI generated comment into a user friendly format. These comments are sent to the
 * frontend in the following format:
 * 6b0e4f64-a38a-4e5d-8a5e-54c4a3a00f0d-OFF_TOPIC-Essay is off-topic.
 * The comment prefix (e.g. 6b0e4f64-a38a-4e5d-8a5e-54c4a3a00f0d) will be different each
 * time the comment is generated, but the type/code and description should be the same.
 */
export function friendlyAutoscoredComment(autoscoredComment: string | null): string {
  if (!autoscoredComment) return '';
  const code = codeFromAutoscoredComment(autoscoredComment);
  if (code && autoscoredComment.includes(codeWithHyphens(code))) {
    const autoscoredCommentParts = autoscoredComment.split(codeWithHyphens(code));
    if (autoscoredCommentParts.length === 2) {
      return autoscoredCommentParts[1];
    }
  }

  // Return the raw comment if the unscorable type or failure code is not known:
  return autoscoredComment;
}

export function unscorableTypeOrFailureCode(
  autoscoredComment: string | null
): UnscorableTypeOrFailureCode | undefined {
  if (!autoscoredComment) return undefined;
  const code = codeFromAutoscoredComment(autoscoredComment);
  if (code && autoscoredComment.includes(codeWithHyphens(code))) {
    return code;
  }
  return undefined;
}

/**
 * A mark should never has a status of Needs Review and an unscorable type of OK. This function
 * returns true if this invalid state has been set in the Admin Tool or elsewhere.
 */
export function needsReviewAndAutoscoredOK(mark: Partial<BaseTestInstanceResponseMark>): boolean {
  if (!mark.comment) return false;
  return (
    mark.status === MarkStatus.NeedsReview && unscorableTypeOrFailureCode(mark.comment) === 'OK'
  );
}

/**
 * Returns true if a mark has the Assist defined unscorable type of "TOO_SHORT_TO_SEND". This is
 * treated differently from the Vantage defined unscorable type "TOO_SHORT", the latter of which we
 * don't allow a response to be reopened or to block manual marking/approve autoscoring since that
 * would trigger additional Vantage autoscoring.
 */
export function isTooShortToSend(mark: Partial<BaseTestInstanceResponseMark>): boolean {
  if (!mark.comment) return false;
  return unscorableTypeOrFailureCode(mark.comment) === 'TOO_SHORT_TO_SEND';
}
